import React from 'react'
import { Form } from 'antd'
import { useStore, useUnit } from 'effector-react'
import dayjs, { Dayjs } from 'dayjs'
import {
  $investmentForm,
  addInvestment,
  addInvestmentMounted,
  deleteInvestmentPayment,
  deleteInvestmentPaymentMounted,
  editInvestmentPayment,
  editInvestmentPaymentMounted,
  getInvestmentPaymentById,
  getInvestmentPaymentByIdMounted,
  setInvestmentForm,
} from './model'
import { $userProfile } from '../../../../../features/user'
import { investmentTransactionType } from '../../../../../libs/finSystem'
import {
  formatTransactionDateTime,
  parseNaiveDateTime,
} from '../../../../../libs/utils'
import { $wallet as $currentWallet } from '../../details/model'
import { initialInvestmentModalState } from './const'
import { ModalType } from '../types'
import { $walletAllowedList } from '../GeneralTransactionModal/model'
import { Currency } from '../../../../../views/widgets/walletCard/types'
import { TransactionModalLayout } from '../TransactionModalLayout'
import { useTransactionForm } from '../useTransactionForm'
import {
  AmountSentReceivedRow,
  CommentField,
  CommissionRow,
  DateTimeRow,
  ExchangeRateRow,
  OperationTypeSelect,
  ProductSelect,
  TextField,
  WalletSelect,
  useDefaultCurrencyOnce,
} from '../transactionFields'

type Props = {
  isOpen: boolean
  allCurrencies: Currency[]
  onClose: () => void
  onCancel?: () => void
  mode?: 'create' | 'edit'
  transactionId?: string
  transactionType?: string
  type: ModalType
}

type FormValues = {
  walletId: string
  operationType: string
  product: string
  fromWallet: string
  sentAmount: string
  sentCurrency: string
  receivedAmount: string
  receivedCurrency: string
  sentCommission: string
  sentCommissionCurrency: string
  receivedCommission: string
  receivedCommissionCurrency: string
  sentExchangeRate: string
  receivedExchangeRate: string
  date: Dayjs | null
  time: Dayjs | null
  comment: string
}

export const InvestmentTransactionModal: React.FC<Props> = ({
  isOpen,
  allCurrencies,
  onClose,
  onCancel,
  transactionId,
  transactionType: initialOperationType,
  mode = 'create',
  type,
}) => {
  const [form] = Form.useForm<FormValues>()

  const userProfile = useStore($userProfile)
  const allWallets = useUnit($walletAllowedList)
  const currentWallet = useStore($currentWallet)

  const walletId = Form.useWatch('walletId', form)

  useDefaultCurrencyOnce(form, 'sentCurrency', 'sentCommissionCurrency')
  useDefaultCurrencyOnce(form, 'receivedCurrency', 'receivedCommissionCurrency')

  const toWalletCurrencies = React.useMemo(() => {
    if (currentWallet.id) {
      return currentWallet.localBalances?.map((item) => item.currency) ?? []
    }
    const w = allWallets.find((item) => Number(item.id) === Number(walletId))
    return w?.currencies ?? []
  }, [allWallets, walletId, currentWallet])

  const allCurrencyCodes = React.useMemo(
    () => (allCurrencies ?? []).map((item) => item.code),
    [allCurrencies],
  )

  const {
    storeData: investmentForm,
    handleSubmit,
    handleSubmitAndCreateNew,
    handleCancel,
    handleDelete,
    isSubmitting,
    isDeleting,
    isLoadingForm,
  } = useTransactionForm({
    form,
    mode,
    transactionId,
    isOpen,
    onClose,
    onCancel,
    store: $investmentForm,
    initialState: initialInvestmentModalState,
    setForm: setInvestmentForm,
    mapStoreToForm: (data) => {
      const dt = parseNaiveDateTime(data.dateTime)
      return {
        walletId: data.walletId,
        operationType: initialOperationType ?? data.operationType,
        product: data.product,
        fromWallet: data.fromWallet,
        sentAmount: String(data.sentAmount ?? ''),
        sentCurrency: data.sentCurrency,
        receivedAmount: String(data.receivedAmount ?? ''),
        receivedCurrency: data.receivedCurrency,
        sentCommission: String(data.sentCommission ?? '0'),
        sentCommissionCurrency: data.sentCommissionCurrency,
        receivedCommission: String(data.receivedCommission ?? '0'),
        receivedCommissionCurrency: data.receivedCommissionCurrency,
        sentExchangeRate: String(data.sentExchangeRate ?? '0'),
        receivedExchangeRate: String(data.receivedExchangeRate ?? '0'),
        date: dt ? dayjs(dt.date) : dayjs(),
        time: dt ? dayjs(dt.time, 'HH:mm:ss') : dayjs(),
        comment: data.comment,
      }
    },
    events: {
      addMount: addInvestmentMounted,
      editMount: editInvestmentPaymentMounted,
      deleteMount: deleteInvestmentPaymentMounted,
      getByIdMount: getInvestmentPaymentByIdMounted,
    },
    effects: {
      add: addInvestment,
      edit: editInvestmentPayment,
      delete: deleteInvestmentPayment,
      getById: getInvestmentPaymentById,
    },
    buildDeletePayload: (data) => data,
    historyFields: { fromWallet: 'finSystem.investor' },
    buildPayload: (values, { mode: m, transactionId: tid }) => {
      const wallet =
        values.walletId || (currentWallet.id ? String(currentWallet.id) : '')
      return {
        ...values,
        id: m === 'edit' ? tid : '',
        walletId: wallet,
        toWallet: wallet,
        sentCommission: values.sentCommission || '0',
        receivedCommission: values.receivedCommission || '0',
        sentExchangeRate: values.sentExchangeRate || '0',
        receivedExchangeRate: values.receivedExchangeRate || '0',
        dateTime: formatTransactionDateTime(
          values.date!.format('YYYY-MM-DD'),
          values.time!.format('HH:mm:ss'),
        ),
      } as any
    },
  })

  return (
    <TransactionModalLayout
      isOpen={isOpen}
      onClose={handleCancel}
      title={`${mode === 'edit' ? 'Edit' : 'Create'} investment${investmentForm.id ? ` #${investmentForm.id}` : ''}`}
      mode={mode}
      creatorEmail={userProfile.email}
      isLoading={isLoadingForm}
      isSubmitting={isSubmitting}
      isDeleting={isDeleting}
      onSubmit={handleSubmit}
      onSubmitAndCreateNew={
        mode === 'create' ? handleSubmitAndCreateNew : undefined
      }
      onDelete={mode === 'edit' ? handleDelete : undefined}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          walletId:
            type === 'wallet' && currentWallet.id
              ? String(currentWallet.id)
              : '',
          sentCommission: '0',
          receivedCommission: '0',
          sentExchangeRate: '0',
          receivedExchangeRate: '0',
          date: dayjs(),
          time: dayjs(),
        }}
        requiredMark={false}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <WalletSelect
            name="walletId"
            label="To"
            required
            disabled={type === 'wallet' || mode === 'edit'}
            style={{ flex: 1 }}
          />
          <OperationTypeSelect
            name="operationType"
            required
            disabled={mode === 'edit'}
            options={investmentTransactionType}
            style={{ flex: 1 }}
          />
        </div>

        <ProductSelect name="product" />

        <TextField
          name="fromWallet"
          label="From"
          placeholder="Investor / source"
          required
          historyScope="finSystem.investor"
        />

        <AmountSentReceivedRow
          sentAmountName="sentAmount"
          sentCurrencyName="sentCurrency"
          sentCurrencies={allCurrencyCodes}
          sentAllowNegative
          sentCurrencyAllowClear
          receivedAmountName="receivedAmount"
          receivedCurrencyName="receivedCurrency"
          receivedCurrencies={toWalletCurrencies}
          receivedRequired
          receivedAllowNegative
          receivedCurrencyRequired
        />

        <CommissionRow
          sentName="sentCommission"
          sentCurrencyName="sentCommissionCurrency"
          sentCurrencies={allCurrencyCodes}
          receivedName="receivedCommission"
          receivedCurrencyName="receivedCommissionCurrency"
          receivedCurrencies={allCurrencyCodes}
        />

        <ExchangeRateRow
          sentName="sentExchangeRate"
          receivedName="receivedExchangeRate"
        />

        <DateTimeRow />

        <CommentField />
      </Form>
    </TransactionModalLayout>
  )
}
